/**
 * Avatar URL validation.
 *
 * Gravatar and Auth0's default avatars render generated initials rather than a
 * real photo, so we treat them as "no image" and fall back to our own initials
 * component instead.
 *
 * This lived as three copy-pasted helpers in Header, SideMenu and
 * ProfileImageUploader, each doing `imageUrl.includes("gravatar.com")`.
 * CodeQL flagged all three as `js/incomplete-url-substring-sanitization`, and
 * it was right: a substring test does not identify a host. It matches
 * `https://evil.example/?ref=gravatar.com` (wrong reject) and
 * `https://gravatar.com.evil.example/x` (also matched, but for the wrong
 * reason). Parse the URL and compare the hostname instead.
 */

const isHost = (hostname: string, domain: string): boolean =>
	hostname === domain || hostname.endsWith(`.${domain}`);

/**
 * True when `imageUrl` is a usable avatar we should render.
 *
 * Rejects empty values, the literal strings "null"/"undefined" (which arrive
 * from upstream JSON that stringified a missing value), anything that is not a
 * parseable absolute URL, and the generated-initials providers.
 */
export const isValidAvatarUrl = (
	imageUrl: string | null | undefined,
): imageUrl is string => {
	if (
		imageUrl == null ||
		imageUrl.trim() === "" ||
		imageUrl === "null" ||
		imageUrl === "undefined"
	) {
		return false;
	}

	let url: URL;
	try {
		url = new URL(imageUrl);
	} catch {
		// Not an absolute URL — nothing sensible to render.
		return false;
	}

	if (url.protocol !== "http:" && url.protocol !== "https:") return false;

	const hostname = url.hostname.toLowerCase();

	// Gravatar always answers with generated initials/identicons.
	if (isHost(hostname, "gravatar.com")) return false;

	// Auth0 serves its own initials avatars under /avatars on its CDN hosts.
	if (isHost(hostname, "auth0.com") && url.pathname.startsWith("/avatars")) {
		return false;
	}

	return true;
};
